/* frontend/src/components/ArticleForm.js */
import React, { useState, useEffect, useCallback, memo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { getArticle, createArticle, updateArticle } from '../utils/api';
import '../pages/Blog.css';

// Error Boundary Component
class ErrorBoundary extends React.Component {
  state = { error: null };
  static getDerivedStateFromError(error) {
    return { error: error.message };
  }
  render() {
    if (this.state.error) {
      return (
        <div className="linkedin-blog container">
          <div className="bg-red-100 text-red-700 p-4 rounded mb-4">
            Error: {this.state.error}. Please refresh or contact support.
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

const ArticleForm = memo(({ user }) => {
  const { id: articleId } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    title: '',
    excerpt: '',
    content: '',
    keywords: '',
    thumbnail: null,
  });
  const [error, setError] = useState(null);

  // Log re-renders
  useEffect(() => {
    console.log('ArticleForm re-rendered', { formData, user, articleId });
  }, [formData, user, articleId]);

  // Fetch CSRF token
  useEffect(() => {
    console.log('Fetching CSRF token');
    axios
      .get(`${process.env.REACT_APP_API_URL}/api/get-csrf-token/`, {
        withCredentials: true,
      })
      .then(() => console.log('CSRF token fetched'))
      .catch((err) => console.error('Failed to fetch CSRF token:', err));
  }, []);

  const { data: article, error: queryError } = useQuery({
    queryKey: ['article', articleId],
    queryFn: () => getArticle(articleId).then((res) => res.data),
    enabled: !!articleId,
  });

  useEffect(() => {
    if (article) {
      console.log('Setting formData from article:', article);
      setFormData({
        title: article.title,
        excerpt: article.excerpt,
        content: article.content, // Backend sends HTML, converted to text
        keywords: article.keywords,
        thumbnail: null,
      });
    }
  }, [article]);

  const mutation = useMutation({
    mutationFn: (data) =>
      articleId ? updateArticle(articleId, data) : createArticle(data),
    onSuccess: () => {
      console.log('Article saved successfully');
      setError(null);
      navigate('/blog');
    },
    onError: (error) => {
      console.error('Article submission error:', error);
      let errorMessage = 'Failed to save article. Please try again.';
      if (error.response?.status === 405) {
        errorMessage = 'Invalid request method. Contact support.';
      } else if (error.response?.status === 403) {
        errorMessage = 'Permission denied. Ensure you are logged in as an admin.';
      } else if (error.response?.data?.errors) {
        errorMessage = JSON.stringify(error.response.data.errors);
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      }
      setError(errorMessage);
    },
  });

  const handleChange = useCallback((e) => {
    const { name, value, files } = e.target;
    console.log('handleChange:', { name, value, files });
    setFormData((prev) => ({
      ...prev,
      [name]: files ? files[0] : value,
    }));
  }, []);

  const handleSubmit = useCallback((e) => {
    e.preventDefault();
    console.log('handleSubmit:', formData);
    setError(null);
    const data = new FormData();
    data.append('author', user.id);
    for (const key in formData) {
      if (key === 'content' && formData[key]) {
        data.append(key, JSON.stringify({ ops: [{ insert: formData[key] + '\n' }] }));
      } else if (formData[key]) {
        data.append(key, formData[key]);
      }
    }
    mutation.mutate(data);
  }, [formData, user, mutation]);

  if (!user || !user.is_staff) {
    console.log('Access denied:', { user });
    return <div className="linkedin-blog container">Access Denied</div>;
  }

  if (queryError) {
    console.error('Query error:', queryError);
    return (
      <div className="linkedin-blog container">
        <div className="bg-red-100 text-red-700 p-4 rounded mb-4">
          Failed to load article: {queryError.message}
        </div>
      </div>
    );
  }

  // Animation variants for form fields
  const fieldVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: (i) => ({
      opacity: 1,
      x: 0,
      transition: { delay: i * 0.1, duration: 0.3 },
    }),
  };

  // Button hover animation
  const buttonVariants = {
    hover: {
      scale: 1.05,
      boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.2)',
      transition: { duration: 0.2 },
    },
  };

  return (
    <ErrorBoundary>
      <motion.div
        className="linkedin-blog"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <div className="container">
          <motion.h1
            className="text-3xl mb-4"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            {articleId ? 'Edit Article' : 'New Article'}
          </motion.h1>
          <AnimatePresence>
            {error && (
              <motion.div
                className="bg-red-100 text-red-700 p-4 rounded mb-4"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.2 }}
              >
                {error}
              </motion.div>
            )}
          </AnimatePresence>
          <form onSubmit={handleSubmit}>
            <motion.input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              placeholder="Title"
              className="w-full p-2 border rounded mb-4 text-[#333333] bg-white focus:outline-none focus:ring-2 focus:ring-[#0a66c2]"
              variants={fieldVariants}
              initial="hidden"
              animate="visible"
              custom={0}
            />
            <motion.textarea
              name="excerpt"
              value={formData.excerpt}
              onChange={handleChange}
              placeholder="Excerpt"
              rows="3"
              className="w-full p-2 border rounded mb-4 text-[#333333] bg-white focus:outline-none focus:ring-2 focus:ring-[#0a66c2]"
              variants={fieldVariants}
              initial="hidden"
              animate="visible"
              custom={1}
            />
            <motion.textarea
              name="content"
              value={formData.content}
              onChange={handleChange}
              placeholder="Article content"
              rows="10"
              className="w-full p-2 border rounded mb-4 text-[#333333] bg-white focus:outline-none focus:ring-2 focus:ring-[#0a66c2]"
              variants={fieldVariants}
              initial="hidden"
              animate="visible"
              custom={2}
            />
            <motion.input
              type="text"
              name="keywords"
              value={formData.keywords}
              onChange={handleChange}
              placeholder="Keywords (comma-separated)"
              className="w-full p-2 border rounded mb-4 text-[#333333] bg-white focus:outline-none focus:ring-2 focus:ring-[#0a66c2]"
              variants={fieldVariants}
              initial="hidden"
              animate="visible"
              custom={3}
            />
            <motion.input
              type="file"
              name="thumbnail"
              accept="image/*"
              onChange={handleChange}
              className="mb-4 text-[#333333]"
              variants={fieldVariants}
              initial="hidden"
              animate="visible"
              custom={4}
            />
            <motion.button
              type="submit"
              className="btn-primary"
              variants={buttonVariants}
              whileHover="hover"
            >
              Save Article
            </motion.button>
          </form>
        </div>
      </motion.div>
    </ErrorBoundary>
  );
});

export default ArticleForm;
